import * as React from 'react'

import { useToggle } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import type { Action } from 'modules/automations/store/models/Action'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'

import { CommonNotificationSelects } from '../../CommonNotification/Selects'
import { RecipientTypes, Recipients } from '../../CommonNotification/utils'
import BodyVariables from './BodyVariables'
import Item from './BodyVariables/Item'
import { variablesCategoriesList, variablesList } from './BodyVariables/variables'
import type { Category, VariableItem } from './BodyVariables/variables'
import { defaultRecipientTypes, groupOptions, recipientTypes, userOptions } from './options'

interface Props {
  action: Action
  disabled: boolean
  form: Form
  contractVersions: ContractVersions
}

const SelectsClass = ({ action, disabled, form, contractVersions }: Props) => {
  const [showVariables, setShowVariables] = useToggle(false)

  const recipientType = form.get('recipient_type').value

  useUnsafeEffect(() => {
    if (recipientType === RecipientTypes.manualSelection) {
      form.setValues({
        recipient: Recipients.access,
      })
    } else if (form.get('recipient').value === Recipients.access) {
      form.setValues({
        recipient: null,
      })
    }
  }, [form, recipientType])

  const recipientOptions = (): SelectOption[] => {
    const trigger = action.automation?.trigger

    if (!trigger) return []

    const recipientType = form.get('recipient_type').value

    if (!recipientType) return []

    switch (recipientType) {
      case RecipientTypes.user:
        return userOptions(trigger.attributes.slug)
      case RecipientTypes.manualSelection:
        return []
      default:
        return groupOptions()
    }
  }

  const getRecipientTypes = (): SelectOption[] => {
    const trigger = action.automation?.trigger

    if (!trigger) return defaultRecipientTypes()

    return recipientTypes(trigger.attributes.slug)
  }

  const onSelectBodyVariable = (variable: string, isCustomTag = false): void => {
    const body = form.get('body_template_body').value
    const tag = isCustomTag ? `{%${variable}%}` : `{{${variable}}}`
    form.setValues({
      body_template_body: `${body} ${tag}`,
    })
    setShowVariables(false)
  }

  const bodyVariablesList = (): VariableItem[] => {
    const trigger = action.automation?.trigger

    if (!trigger) return []

    return variablesList(trigger)
  }

  const bodyVariablesCategoriesList = (): Category[] => {
    const trigger = action.automation?.trigger

    if (!trigger) return []

    return variablesCategoriesList(trigger)
  }

  const renderBodyVariableItem = (item: VariableItem) => {
    return <Item key={item.payloadVariable} item={item} onSelect={onSelectBodyVariable} />
  }

  return (
    <>
      <CommonNotificationSelects
        multi
        form={form}
        disabled={disabled}
        recipientOptions={recipientOptions()}
        recipientTypeOptions={getRecipientTypes()}
        contractVersions={contractVersions}
      />
      <FormRow>
        <Text
          field={form.get('subject_template_body')}
          label={i18n.t('automations.actions.send_custom_notification.subject.label')}
          readonly={disabled}
        />
      </FormRow>
      {!disabled && (
        <BodyVariables
          items={bodyVariablesList().map(renderBodyVariableItem)}
          categories={bodyVariablesCategoriesList()}
          showVariables={showVariables}
          toggleShowVariables={setShowVariables}
        />
      )}
      <FormRow>
        <TextArea
          field={form.get('body_template_body')}
          label={i18n.t('automations.actions.send_custom_notification.body.label')}
          minRows={4}
          readonly={disabled}
        />
      </FormRow>
    </>
  )
}

export default observer(SelectsClass)
